import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useAuthenticator } from "@aws-amplify/ui-react";
import {
  Button,
  TextField,
  Box,
  CircularProgress,
  Typography,
  Modal,
  Paper,
} from "@mui/material";
import { Image } from "@aws-amplify/ui-react";
import { Card, Flex } from "@aws-amplify/ui-react";
import { Amplify, API, Storage } from "aws-amplify";
import awsmobile from "../aws-exports";
import { listProducts } from "../graphql/queries";
import { createProductSampleRequest } from "../graphql/mutations";
// import PriceInUSD from "./PriceInUSD"
// import PriceInUSDCard from "./PriceInUSDCard"

export function HomeDistributor() {

  const [searchQuery, setSearchQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [currentImage, setCurrentImage] = useState();
  const [euroToUSDRate, setEuroToUSDRate] = useState();

  async function getSignedUri(key) {
    try {
      console.log("Fetching signed URL for key:", key); // Debug the key
      const url = await Storage.get(key, { level: "public" });
      console.log("Generated Signed URL:", url); // Debug the signed URL
      return url;
    } catch (err) {
      console.error("Error generating signed URL:", err);
      return null;
    }
  }

  useEffect(() => {

    const getRate = async () => {
      fetch('https://open.er-api.com/v6/latest/EUR')
      .then((response) => response.json())
      .then((data) => {
        setEuroToUSDRate(data.rates.USD);
      })
      .catch((error) => {
        console.error('Error fetching exchange rates:', error);
      });
    };

    const checkProducts = async () => {
      console.log("Configuring Amplify...");
      await new Promise((resolve) => {
        Amplify.configure({
          ...awsmobile,
          aws_appsync_authenticationType: "API_KEY",
        });
        Storage.configure({ track: true, level: "public" });
        resolve(); // Resolve immediately since Amplify.configure itself is not async
      });

      console.log("Starting product fetch...");
      setLoading(true);
      setError(null);

      async function fetchProducts() {
        try {
          const response = await API.graphql({ 
              query: listProducts,
              variables: {
                filter: {
                  _deleted: { ne: true }
                }
              }
           });
          // console.log("Raw response from listProducts:", response);
          return response?.data?.listProducts?.items || [];
        } catch (err) {
          console.error("Error fetching products:", err);
          throw err;
        }
      }

      try {
        const [products] = await Promise.all([
          fetchProducts(),
        ]);
        console.log("Fetched products and images:", products);
        setProducts(
          products.map((product) => ({
            ...product,
            image: "placeholder.jpg",
          }))
        );
      } catch (err) {
        console.error("Error in checkProducts:", err);
        setError("Failed to load products and images. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    const initialize = async () => {
      await getRate(); // Ensure this completes before moving on
      await checkProducts(); // Now this will run after getRate()
    };
  
    initialize(); // Start the async sequence
    
    return () => {
      console.log("Cleanup if necessary...");
    };
  }, []); // Dependency array should be empty to run once

  const handleRowClick = (params) => {
    const imageKey = params.row.product_image.replace("s3://", "");
    async function getImage() {
      try {
        const signedUri = await getSignedUri(imageKey);
        setCurrentImage(signedUri); // Set the signed URL in state
      } catch (error) {
        console.error("Error fetching signed URL:", error);
        setCurrentImage("https://via.placeholder.com/250"); // Fallback image
      }
    }
    getImage();
    setSelectedRow(params.row);
    setOpenModal(true);
  };
  

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedRow(null);
  };

  const [sampleModalOpen, setSampleModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const handleRequestSample = (event, product) => {
    event.stopPropagation(); // Prevents the row click event
    // console.log("Selected Product:", product); // Debugging
    setSelectedProduct(product); // Set the selected product
    setSampleModalOpen(true); // Open the modal
  };

  const handleCloseSampleModal = () => {
    setSampleModalOpen(false); // Close the modal
    setSelectedProduct(null); // Reset the selected product
  };

  async function handleRequestSampleEmail(user, product) {
  
    // Extract necessary information from user and product objects
    const d_contact_name = user.attributes["custom:d_contact_name"];
    const d_code = user.attributes["custom:d_code"];
    const d_contact_phone = user.attributes["custom:d_contact_phone"];
    const d_email = user.attributes.email;
    const brand_name = product.brand_name;
    const brand_product_name = product.product_name;
    const brand_registered_region = product.registered_region;
    const brand_price_ex_vat = (product.price_ex_vat * euroToUSDRate).toFixed(2)

    // Prepare the input object for the mutation
    const input = {
      distributor_name: d_contact_name, // Distributor's name
      distributor_code: d_code, // Distributor's unique code
      distributor_phone: d_contact_phone, // Distributor's contact phone
      distributor_email: d_email, // Distributor's email
      brand_name: brand_name, // Brand's name
      product_name: brand_product_name, // Product's name
      price_ex_vat: brand_price_ex_vat, // Price excluding VAT
      registered_region: brand_registered_region, // Brand's registered region
    };

    try {
      // Execute the GraphQL mutation
      const response = await API.graphql({
        query: createProductSampleRequest,
        variables: { input },
      });

      // Log the response for debugging
      console.log(
        "Sample request created successfully:",
        response.data.createProductSampleRequest
      );
    } catch (error) {
      // Handle and log any errors
      console.error("Error creating product sample request:", error);
    }
  }

  const filteredProducts = products.filter(
    (product) =>
      product.product_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.brand_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const columns = [
    { 
      field: "brand_name", 
      headerName: "Brand Name", 
      flex: 1, 
      minWidth: 250,
    },
    {
      field: "product_name",
      headerName: "Product Name",
      flex: 2,
      minWidth: 300,
    },
    {
      field: "registered_region",
      headerName: "Region",
      flex: 1,
      minWidth: 100,
    },
    { field: "style", headerName: "Style", flex: 1, minWidth: 150 },
    {
      field: "price_ex_vat",
      headerName: "FOB Price^ (per bottle) in USD",
      flex: 1,
      minWidth: 300,
    },
    {
      field: "bottles_per_year",
      headerName: "Bottles/Year",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "year_of_production",
      headerName: "Year",
      flex: 0.8,
      minWidth: 100,
    },
    {
      field: "container_size_cl",
      headerName: "Size (cl)",
      flex: 0.8,
      minWidth: 100,
    },
    {
      field: "abv_percentage",
      headerName: "ABV (%)",
      flex: 0.8,
      minWidth: 100,
    },
    {
      field: "request_sample",
      headerName: "Request Sample",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#000000",
            color: "#ffffff",
            padding: "8px 16px",
            textTransform: "none",
            fontWeight: "bold",
            "&:hover": {
              backgroundColor: "#333333",
            },
          }}
          onClick={(event) => handleRequestSample(event, params.row)}
        >
          Request Sample
        </Button>
      ),
      sortable: false,
    },
  ];

  const rows = filteredProducts.map((product) => ({
    ...product, // Spread the product object to retain all fields
    id: product.id, // Explicitly define the ID for DataGrid usage
    //price_ex_vat: (product.price_ex_vat * euroToUSDRate).toFixed(2)
    price_ex_vat: `Price min = ${(product.price_ex_vat * euroToUSDRate).toFixed(2)}; Price max = ${(product.price_ex_vat * euroToUSDRate * 2.5).toFixed(2)}`

  }));

  const { user } = useAuthenticator((context) => [context.user]);

  return (
    <Box
      sx={{
        marginTop: "30px",
        padding: "2rem",
        display: "flex",
        justifyContent: "center",
        height: "100vh", // Use full viewport height
        overflow: "hidden", // Prevent page scrolling
      }}
    >
      <Box sx={{ 
        width: "95%",
        margin: "0 auto",
        height: "100vh", // Fixed height to make the table container scrollable
        display: "flex",
        flexDirection: "column",
      }}
      
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography variant="h6" color="error" align="center">
            {error}
          </Typography>
        ) : (
          <>
            <Box 
              sx={{ 
                
                width: "95%",
                margin: "0 auto",
                height: "83vh", // Fixed height to make the table container scrollable
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
              
              }}
            >
              <div
                style={{
                  textAlign: "center", // Center align for a balanced layout
                  // marginBottom: "40px", // Space below the header
                  padding: "10px", // Inner spacing for layout clarity
                }}
              >
                <h1
                  style={{
                    fontFamily: "'Varela Round', sans-serif",
                    fontSize: "22px",
                    marginBottom: "15px",
                    color: "black", // Unified modern grey for titles
                    lineHeight: "1.2",
                  }}
                >
                  Explore and Request Your Samples
                </h1>
                <p
                  style={{
                    fontFamily: "'Varela Round', sans-serif",
                    fontSize: "16px",
                    fontWeight: "400",
                    color: "#666", // Consistent grey for body text
                    lineHeight: "1.6",
                    margin: "0 auto", // Center content
                    maxWidth: "600px", // Constrain width for better focus
                  }}
                >
                  Browse our curated selection of premium Italian wines and spirits. Choose the products you'd like to sample, and we'll take care of the rest!
                </p>
              </div>
              <Box sx={{ marginBottom: "20px", display: "flex", justifyContent: "center" }}>
                <TextField
                  variant="outlined"
                  // label="Search Products"
                  label="Use the search bar to find your favorites quickly"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{
                    width: "50%",
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                  }}
                />
              </Box>

              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  border: "1px solid #e0e0e0",
                  borderRadius: "16px",
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.2)",
                }}
              >
                {/* DataGrid Table */}
                <DataGrid
                  rows={rows}
                  columns={columns}
                  autoHeight
                  pageSize={5}
                  rowsPerPageOptions={[5, 10, 20]}
                  disableSelectionOnClick
                  onRowClick={handleRowClick}
                  sx={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #e0e0e0",
                    borderRadius: "16px",
                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.2)",
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundImage:
                        "linear-gradient(to right, rgb(159, 107, 62), gold)",
                      fontWeight: "bold",
                      color: "#000000",
                      borderBottom: "1px solid #e0e0e0",
                      fontSize: "1.1rem",
                      borderRadius: "16px 16px 0 0",
                    },
                    "& .MuiDataGrid-cell": {
                      color: "#333333",
                      fontSize: "0.95rem",
                      borderBottom: "1px solid rgba(224, 224, 224, 0.8)",
                    },
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "rgba(255, 215, 0, 0.2)",
                      cursor: "pointer", // Indicate the row is clickable
                    },
                    "& .MuiDataGrid-footerContainer": {
                      backgroundColor: "#fafafa",
                      borderTop: "1px solid #e0e0e0",
                      borderRadius: "0 0 16px 16px",
                    },
                  }}
                />
              </Box>

              {/* Loading Spinner Overlay */}
              {loading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(255, 255, 255, 0.7)", // Semi-transparent background
                    zIndex: 2, // Ensure it overlays the table
                  }}
                >
                  <CircularProgress />
                </Box>

                
              )}
             
             <>
             <br></br>
                <h4 style={{ color: "#666" }}>
                  ^ Price is an indication. Price depends on order quantity, State delivery and time of the order.
                </h4>
              </>
              
            </Box>

            {selectedRow && (
              <Modal open={openModal} onClose={handleCloseModal}>
                <Paper
                  sx={{
                    padding: "2rem",
                    margin: "7% auto",
                    maxWidth: "1200px", // Wider modal
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
                    overflowY: "auto",
                    maxHeight: "100vh",
                    position: "relative", // For positioning the X button
                  }}
                >
                  {/* X Button */}
                  <Button
                    onClick={handleCloseModal}
                    sx={{
                      position: "absolute",
                      top: "10px",
                      right: "10px",
                      backgroundColor: "#000",
                      color: "#fff",
                      minWidth: "40px",
                      minHeight: "40px",
                      borderRadius: "50%",
                      fontSize: "1rem",
                      lineHeight: 1,
                      padding: 0,
                      "&:hover": {
                        backgroundColor: "#444",
                      },
                    }}
                  >
                    x
                  </Button>

                  <Card
                    borderRadius="medium"
                    variation="elevated"
                    style={{
                      padding: "1.5rem",
                      border: "1px solid #ccc",
                      boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
                      display: "flex",
                      alignItems: "stretch",
                    }}
                  >
                    <Flex direction="row" gap="2rem" style={{ flex: 1 }}>
                      {/* Image Section */}
                      <Flex
                        justifyContent="center"
                        alignItems="center"
                        style={{
                          flex: "0 0 300px", // Adjusted width for larger image section
                          height: "100%",
                          maxWidth: "150px"
                        }}
                      >
                        <Image
                          width="100%"
                          maxHeight="400px"
                          height="auto"
                          style={{ objectFit: "contain" }}
                          src={currentImage || "https://via.placeholder.com/250"}
                          alt="Product Image"
                        />
                      </Flex>

                      {/* Content Section */}
                      <Flex
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        gap="1.5rem"
                        style={{ flex: 1 }}
                      >
                        {/* Top Section: Brand Name and Description */}
                        <Flex
                          direction="column"
                          justifyContent="flex-start"
                          alignItems="flex-start"
                          gap="0.5rem"
                        >
                          {/* Brand Name */}
                          <Flex
                            justifyContent="space-between"
                            alignItems="center"
                            style={{ width: "100%" }}
                          >
                            <h3
                              style={{
                                margin: 0,
                                fontSize: "1.5rem",
                                textAlign: "left",
                              }}
                            >
                              {selectedRow.brand_name}
                            </h3>
                          </Flex>

                          {/* Product Name and Year */}
                          <p
                            style={{
                              margin: 0,
                              fontWeight: "bold",
                              fontSize: "1.2rem",
                              textAlign: "left",
                            }}
                          >
                            {selectedRow.product_name} (
                            {selectedRow.year_of_production})
                          </p>

                          {/* Description */}
                          {selectedRow.techSheetDescription && (
                            <p
                              style={{
                                margin: 0,
                                fontSize: "1rem",
                                textAlign: "left",
                              }}
                            >
                              {selectedRow.techSheetDescription}
                            </p>
                          )}
                        </Flex>

                        {/* Middle Section */}
                        <Flex direction="row" gap="2rem">
                          {/* General Information */}
                          <Flex
                            direction="column"
                            justifyContent="flex-start"
                            alignItems="flex-start"
                            gap="0.75rem"
                            style={{ flex: 1 }}
                          >
                            <h5
                              style={{
                                margin: 0,
                                fontSize: "1.2rem",
                                textDecoration: "underline",
                                textAlign: "left",
                              }}
                            >
                              GENERAL INFORMATION
                            </h5>
                            <div style={{ marginLeft: "1rem" }}>
                              <div>
                                <strong>Region:</strong>{" "}
                                {selectedRow.registered_region}
                              </div>
                              <div>
                                <strong>Style:</strong> {selectedRow.style}
                              </div>
                              {/* <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <strong>Price:</strong>
                                <PriceInUSDCard priceInEUR={selectedRow.price_ex_vat} />
                              </div> */}
                              <div>
                                <strong>Bottles Per Year:</strong>{" "}
                                {selectedRow.bottles_per_year}
                              </div>
                              <div>
                                <strong>Container Size:</strong>{" "}
                                {selectedRow.container_size_cl} cl
                              </div>
                              <div>
                                <strong>ABV:</strong>{" "}
                                {selectedRow.abv_percentage}%
                              </div>
                            </div>
                          </Flex>

                          {/* Sensory Characteristics */}
                          <Flex
                            direction="column"
                            justifyContent="flex-start"
                            alignItems="flex-start"
                            gap="1rem"
                            style={{ flex: 1 }}
                          >
                            <h5
                              style={{
                                margin: 0,
                                fontSize: "1.2rem",
                                textDecoration: "underline",
                                textAlign: "left",
                              }}
                            >
                              SENSORY CHARACTERISTICS
                            </h5>
                            <div style={{ marginLeft: "1rem" }}>
                              <div>
                                <strong>Appearance:</strong>{" "}
                                {selectedRow.techSheetAppearance ||
                                  "Not Available"}
                              </div>
                              <div>
                                <strong>Nose:</strong>{" "}
                                {selectedRow.techSheetNose || "Not Available"}
                              </div>
                              <div>
                                <strong>Palate:</strong>{" "}
                                {selectedRow.techSheetPalate || "Not Available"}
                              </div>
                              <div>
                                <strong>Pairings:</strong>{" "}
                                {selectedRow.techSheetPairings?.join(", ") ||
                                  "Not Available"}
                              </div>
                              <div>
                                <strong>Serving Temp:</strong>{" "}
                                {selectedRow.techSheetServingTemperatureMin
                                  ? `${selectedRow.techSheetServingTemperatureMin} - ${selectedRow.techSheetServingTemperatureMax} °C`
                                  : "Not Available"}
                              </div>
                              <div>
                                <strong>Certifications:</strong>{" "}
                                {selectedRow.techSheetCertifications?.join(
                                  ", "
                                ) || "Not Available"}
                              </div>
                            </div>
                          </Flex>
                        </Flex>
                      </Flex>
                    </Flex>
                  </Card>
                </Paper>
              </Modal>
            )}

            {/* Request Sample Modal */}
            <Modal open={sampleModalOpen} onClose={handleCloseSampleModal}>
              <Paper
                sx={{
                  padding: "2rem",
                  margin: "15% auto",
                  maxWidth: "500px",
                  textAlign: "center",
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.2)",
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #f5f5f5, #ffffff)",
                }}
              >
                {/* Modal Header */}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    fontSize: "1.5rem",
                    color: "#333",
                    marginBottom: "1rem",
                  }}
                >
                  Confirm Sample Request
                </Typography>

                {/* Modal Content */}
                <Typography
                  sx={{
                    fontSize: "1rem",
                    color: "#555",
                    marginBottom: "1.5rem",
                  }}
                >
                  Are you sure you want to request a sample for
                  <br />
                  <Box
                    component="strong"
                    sx={{
                      display: "block",
                      marginTop: "0.5rem",
                      fontSize: "1.2rem",
                      color: "#333", // Slightly darker color for emphasis
                      textAlign: "center", // Center align for better presentation
                    }}
                  >
                    <Box component="span" sx={{ display: "block", fontWeight: "bold" }}>
                      "{selectedProduct?.brand_name}"
                    </Box>
                    <Box component="span" sx={{ display: "block", fontStyle: "italic", marginTop: "0.2rem" }}>
                      "{selectedProduct?.product_name}"
                    </Box>
                  </Box>
                  <br />
                  Merum staff will contact you shortly to confirm the details.
                </Typography>

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "1rem",
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleCloseSampleModal}
                    sx={{
                      backgroundColor: "#d32f2f",
                      color: "#fff",
                      textTransform: "none",
                      fontWeight: "bold",
                      padding: "0.5rem 1.5rem",
                      borderRadius: "8px",
                      "&:hover": {
                        backgroundColor: "#b71c1c",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      // Add logic to handle the confirmation, e.g., API call
                      // alert(
                      //   `Sample requested for "${selectedProduct?.product_name}" with ID: ${selectedProduct?.id}!`
                      // );

                      handleRequestSampleEmail(user, selectedProduct);
                      handleCloseSampleModal();
                    }}
                    sx={{
                      backgroundColor: "#4caf50",
                      color: "#fff",
                      textTransform: "none",
                      fontWeight: "bold",
                      padding: "0.5rem 1.5rem",
                      borderRadius: "8px",
                      "&:hover": {
                        backgroundColor: "#388e3c",
                      },
                    }}
                  >
                    Confirm
                  </Button>
                </Box>
              </Paper>
            </Modal>
          </>
        )}
      </Box>
    </Box>
  );
}
